import { EventNames, globalEventBus } from '@littlebird/event-bus';
import { ChatStatus, isChatStatusBusy, useStores } from '@littlebird/sdk';
import { deriveLoaderActivity } from '@littlebird/ui-utils';
import { comparer, computed } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  LoaderPortal,
  LogoLoaderView,
  useLogoLoader,
} from '@/components/ui/logo-loader';
import {
  localChatEventsBus,
  USER_DID_SEND_MESSAGE_EVENT,
} from '@/features/chat/chat.events';
import { useElementObserver } from '@/hooks/useElementObserver';
import { appStore } from '@/stores/app.store';

import { CHAT_THREAD_ID } from './useChatScroll';

const THREAD_OBSERVER_OPTIONS: MutationObserverInit = {
  childList: true,
  subtree: true,
  attributes: true,
  attributeFilter: ['id', 'class'],
};

const LOADER_EXIT_DEBOUNCE_MS = 500;

type LoaderPhase = 'idle' | 'active' | 'exiting';

interface StreamingLoaderProps {
  threadId: string;
}

export const StreamingLoader = observer(
  ({ threadId }: StreamingLoaderProps) => {
    const { threads, messages } = useStores();

    const threadElementId = `${CHAT_THREAD_ID}-${threadId}`;
    const threadStatus = threads.getById(threadId)?.status;

    const [isLoading, setIsLoading] = useState(
      threadStatus === ChatStatus.Thinking
    );
    const [loaderPhase, setLoaderPhase] = useState<LoaderPhase>(
      threadStatus === ChatStatus.Thinking ? 'active' : 'idle'
    );

    const loaderSignals = useMemo(
      () =>
        computed(
          () => {
            const threadMessages = messages.getMessagesByThreadId(threadId);
            const lastMessage = threadMessages[threadMessages.length - 1];
            return {
              activity: deriveLoaderActivity(lastMessage?.modalitiesUsed),
              hasStreamedContent:
                lastMessage?.role === 'assistant' &&
                lastMessage.content.length > 0,
            };
          },
          { equals: comparer.structural }
        ),
      [messages, threadId]
    );
    const { activity, hasStreamedContent } = loaderSignals.get();

    const loader = useLogoLoader({
      activity,
      isExiting: loaderPhase === 'exiting',
      active: loaderPhase !== 'idle',
      calm: hasStreamedContent,
      onExitComplete: () => setLoaderPhase('idle'),
    });

    const [lastMessageId, setLastMessageId] = useState<
      string | null | undefined
    >();

    const hasConnectionProblem = appStore.hasConnectionProblem;

    const handleDOMUpdates = useCallback(() => {
      const threadListElement =
        document.getElementById(threadElementId)?.firstElementChild;

      // Find all elements with data-message-id and get the last one
      const messageElements =
        threadListElement?.querySelectorAll('[data-message-id]');
      const lastMessageElement = messageElements?.[messageElements.length - 1];

      const messageId = lastMessageElement?.getAttribute('data-message-id');
      setLastMessageId(messageId);
    }, [threadElementId]);

    // Observe the thread element for changes to update the last message ID
    useElementObserver(
      `${CHAT_THREAD_ID}-${threadId}`,
      handleDOMUpdates,
      THREAD_OBSERVER_OPTIONS
    );

    // The event bus delivers server events asynchronously, so a reply that
    // lands in one websocket batch can have its thoughts handled after the
    // thread already reported Done (which switched the loader off). Only a
    // thread that is still busy may switch it back on.
    const startLoadingIfBusy = useCallback(() => {
      if (!isChatStatusBusy(threads.getById(threadId)?.status)) return;
      setIsLoading(true);
      handleDOMUpdates();
    }, [threads, threadId, handleDOMUpdates]);

    // Listen for user message, assistant thought events and streaming stop events
    useEffect(() => {
      const handleUserSendMessage = ({
        threadId: eventThreadId,
      }: {
        threadId: string;
      }) => {
        if (threadId === eventThreadId) {
          setIsLoading(true);
          handleDOMUpdates();
        }
      };

      const handleHiddenTextEvent = ({
        threadId: eventThreadId,
      }: {
        threadId: string;
      }) => {
        if (eventThreadId === threadId) startLoadingIfBusy();
      };

      const handleThoughtEvent = ({
        threadId: eventThreadId,
      }: {
        threadId: string;
      }) => {
        if (eventThreadId === threadId) startLoadingIfBusy();
      };

      const handleStreamingDidStop = ({
        threadId: eventThreadId,
      }: {
        threadId: string;
      }) => {
        if (eventThreadId === threadId) {
          setIsLoading(false);
        }
      };

      const handleThreadError = ({
        threadId: eventThreadId,
      }: {
        threadId: string;
      }) => {
        if (eventThreadId === threadId) {
          setIsLoading(false);
        }
      };

      const userDidSendMessageListener = localChatEventsBus.on(
        USER_DID_SEND_MESSAGE_EVENT,
        ({ data }) => handleUserSendMessage(data)
      );
      const newHiddenTextEventSubscription = globalEventBus.on(
        EventNames.CHAT_NEW_ASSISTANT_HIDDEN_TEXT,
        ({ data }) => handleHiddenTextEvent(data)
      );
      const newAssistantThoughtEventSubscription = globalEventBus.on(
        EventNames.CHAT_NEW_ASSISTANT_THOUGHT,
        ({ data }) => handleThoughtEvent(data)
      );
      const streamingMessageDidStopSubscription = globalEventBus.on(
        EventNames.CHAT_STREAMING_MESSAGE_DID_STOP,
        ({ data }) => handleStreamingDidStop(data)
      );
      const chatErrorSubscription = globalEventBus.on(
        EventNames.CHAT_ERROR,
        ({ data }) => handleThreadError(data)
      );
      // Server errors and client-side send failures both dismiss the optimistic loader, except a
      // message refused while a reply holds the chat, since that reply goes on
      const chatSendFailedSubscription = globalEventBus.on(
        EventNames.CHAT_SEND_FAILED,
        ({ data }) => {
          if (data.reason !== 'chat_busy') handleThreadError(data);
        }
      );

      return () => {
        userDidSendMessageListener.unsubscribe();
        newHiddenTextEventSubscription.unsubscribe();
        newAssistantThoughtEventSubscription.unsubscribe();
        streamingMessageDidStopSubscription.unsubscribe();
        chatErrorSubscription.unsubscribe();
        chatSendFailedSubscription.unsubscribe();
      };
    }, [threadId, handleDOMUpdates, startLoadingIfBusy]);

    useEffect(() => {
      if (hasConnectionProblem && isLoading) {
        setIsLoading(false);
      }
    }, [hasConnectionProblem, isLoading]);

    useEffect(() => {
      if (isLoading) {
        setLoaderPhase('active');
        return;
      }
      const exitDelay = setTimeout(
        () =>
          setLoaderPhase((phase) => (phase === 'active' ? 'exiting' : phase)),
        LOADER_EXIT_DEBOUNCE_MS
      );
      return () => clearTimeout(exitDelay);
    }, [isLoading]);

    useEffect(() => {
      if (
        threadStatus === ChatStatus.Done ||
        threadStatus === ChatStatus.Errored ||
        threadStatus === ChatStatus.WaitingForClientTools
      ) {
        setIsLoading(false);
      } else if (threadStatus === ChatStatus.Thinking) {
        // Re-enter loading when status transitions back to Thinking
        // after tool calls complete in agentic loops (useState initial
        // value only covers mount, not subsequent transitions)
        setIsLoading(true);
      }
    }, [threadStatus]);

    if (!lastMessageId) return null;

    return (
      <LoaderPortal targetId={lastMessageId}>
        {loaderPhase !== 'idle' && <LogoLoaderView {...loader} />}
      </LoaderPortal>
    );
  }
);
